/**
 * @file uvis-definition.ts
 * @module UvisDefinition
 * @description Mapbox GL stilu slāņu definīcijas UVIS (braukšanas apstākļu brīdinājumiem)
 *              iegultajā kartē. Renderē ceļa segmentus ar krāsu kodētu apstākļu
 *              smaguma pakāpi un virziena indikatoriem.
 */

import type { LayerSpecification, SourceSpecification } from "maplibre-gl";
import { IS_LOCALHOST } from "../../../map/Endpoints";

/** Base origin for embed tile requests; uses test server for localhost and map-test environments */
export const EMBED_ORIGIN = IS_LOCALHOST || window.location.origin.includes("map-test") ? "map-test.lvceli.lv" : "map.transportdata.gov.lv";

/**
 * Merged vector tile source for traffic events and construction plans.
 * Combines five PostgreSQL tables into a single MVT request:
 * - mvt_kafkamessages_notplannedevent_public (unplanned events)
 * - mvt_kafkamessages_plannedevent_public (planned events)
 * - mvt_kafkamessages_notplannedevent_detour_public (unplanned detour routes)
 * - mvt_kafkamessages_plannedevent_detour_public (planned detour routes)
 * - mvt_kafkamessages_soconstructionplan_public (construction plans)
 */
export const PGTILES_1: SourceSpecification = {
	tiles: [
		`https://${EMBED_ORIGIN}/gis/pg_tileserv/public.mvt_kafkamessages_notplannedevent_public,public.mvt_kafkamessages_plannedevent_public,public.mvt_kafkamessages_notplannedevent_detour_public,public.mvt_kafkamessages_plannedevent_detour_public,public.mvt_kafkamessages_soconstructionplan_public/{z}/{x}/{y}.pbf`,
	],
	type: "vector",
	promoteId: "kafka_id",
};

/**
 * Merged vector tile source for UVIS road conditions, CMS sensors, and cameras.
 * Combines three PostgreSQL tables into a single MVT request:
 * - mvt_kafkamessages_uvismessage (UVIS road condition segments)
 * - mvt_kafkamessages_cms (CMS weather/road surface sensors)
 * - mvt_kafkamessages_camera (traffic cameras)
 */
export const PGTILES_2: SourceSpecification = {
	tiles: [`https://${EMBED_ORIGIN}/gis/pg_tileserv/public.mvt_kafkamessages_uvismessage,public.mvt_kafkamessages_cms,public.mvt_kafkamessages_camera/{z}/{x}/{y}.pbf`],
	type: "vector",
	promoteId: "kafka_id",
};

/** Vector tile source for LAU (road maintenance) vehicle positions */
export const LAU_TILES: SourceSpecification = {
	tiles: [`https://${EMBED_ORIGIN}/gis/pg_tileserv/public.mvt_lau_transportunit/{z}/{x}/{y}.pbf`],
	type: "vector",
	promoteId: "id",
};

/**
 * UVIS road condition line layer.
 * Colors road segments by condition:
 * - Green (#41C954): satisfying
 * - Blue (#10ACE8): unsatisfying
 * - Orange (#F75600): very unsatisfying
 * - Cyan (#00ccf5): highlighted (feature-state)
 * Only renders LineString and MultiLineString geometries.
 */
export const ROAD_LAYER: LayerSpecification = {
	id: "road_layer",
	source: "pgtiles_2",
	"source-layer": "public.kafkamessages_uvismessage",
	type: "line",
	filter: ["any", ["==", ["geometry-type"], "MultiLineString"], ["==", ["geometry-type"], "LineString"]],
	paint: {
		"line-color": [
			"case",
			["boolean", ["feature-state", "highlighted"], false],
			"#00ccf5",
			["==", ["get", "road_conditions"], "satisfying"],
			"#41C954",
			["==", ["get", "road_conditions"], "unsatisfying"],
			"#10ACE8",
			["==", ["get", "road_conditions"], "very_unsatisfying"],
			"#F75600",
			"#000000",
		],
		"line-width": ["interpolate", ["linear"], ["zoom"], 10, 6, 16, 13],
	},
	layout: {
		"line-join": "round",
		"line-cap": "round",
		"line-round-limit": 0,
	},
};

/**
 * UVIS road condition polygon (fill) layer for area-based warnings.
 * Only shows unsatisfying, very unsatisfying, and extreme conditions:
 * - Blue (#3AADEA): unsatisfying
 * - Red (#BF0000): very unsatisfying
 * - Pink (#B03568): extreme
 * Rendered at 35% opacity. Only shows Polygon geometries.
 */
export const POLY_LAYER: LayerSpecification = {
	id: "poly_layer",
	source: "pgtiles_2",
	"source-layer": "public.kafkamessages_uvismessage",
	type: "fill",
	paint: {
		"fill-color": [
			"case",
			["==", ["get", "road_conditions"], "unsatisfying"],
			"#3AADEA",
			["==", ["get", "road_conditions"], "very_unsatisfying"],
			"#BF0000",
			["==", ["get", "road_conditions"], "extreme"],
			"#B03568",
			"#000000",
		],
		"fill-opacity": 0.35,
	},
	filter: [
		"all",
		["==", ["geometry-type"], "Polygon"],
		["any", ["==", ["get", "road_conditions"], "extreme"], ["==", ["get", "road_conditions"], "very_unsatisfying"], ["==", ["get", "road_conditions"], "unsatisfying"]],
	],
};
